/**
 * Orders Service
 *
 * Service for orders-service API endpoints
 * Handles order management, status tracking, and reporting
 */

import { apiClient } from './apiClient';
import { wsLog } from '@utils/wsLogger';
import type {
  Order,
  OrderItem,
  TodayOrdersParams,
  TodayOrdersResponse,
  OrderSummaryParams,
  OrderSummaryResponse,
  OrderSummaryAllParams,
  OrderSummaryAllResponse,
  UpdateOrderStatusRequest,
  UpdateOrderStatusResponse,
  OrderDetailParams,
  OrderDetailResponse,
  OrderStats,
  OrderDashboardData,
  OrderListParams,
  BatchOrderOperation,
  BatchOrderResult,
  OrderApiResponse,
  KitchenDashboard,
  CurrentIntervalsParams,
  IntervalResponse,
  Interval,
} from './orderTypes';
import type { ApiError } from './types';
import { OrderStatus, ORDER_VALIDATION_RULES } from './orderTypes';
// mockOrdersApi não é mais importado: os pontos que o usavam estão marcados
// "Mock data disabled - using real API" e nenhuma chamada restou. O import
// sozinho mantinha o módulo inteiro no bundle e no typecheck.

const ROUTES = {
  ORDERS: {
    TODAY: '/orders/today',
    SUMMARY: '/orders/summary',
    SUMMARY_ALL: '/orders/summary/all',
    DETAIL: '/orders/detail',
    INTERVALS: '/orders/intervals/current',
  },
} as const;

/**
 * O endpoint GET /orders/today devolve um ARRAY puro de pedidos, mas o tipo
 * declarado (TodayOrdersResponse) promete um objeto com `orders`, `total` e
 * `summary`. Essa divergência entre o tipo e o runtime quebrava, em silêncio,
 * todo consumidor que acreditava no tipo:
 *
 *  - a sobreposição do WebSocket na tela da fila era guardada por
 *    `initialOrdersData?.orders?.length > 0`, que num array é sempre falso —
 *    o pedido mudava de status no evento e o card não saía da coluna;
 *  - getStudentOrders e getOrderSummaryData fazem `response.orders` direto.
 *
 * Normalizar aqui faz o runtime honrar o tipo em todos eles de uma vez.
 */
function normalizeTodayOrders(data: TodayOrdersResponse | Order[] | null | undefined): TodayOrdersResponse {
  const orders: Order[] = Array.isArray(data) ? data : (data?.orders ?? []);

  if (!Array.isArray(data) && data?.summary) {
    return { ...data, orders, total: data.total ?? orders.length };
  }

  const byStatus = orders.reduce(
    (acc, order) => {
      acc[order.status] = (acc[order.status] || 0) + 1;
      return acc;
    },
    {} as Record<OrderStatus, number>,
  );

  return {
    orders,
    total: orders.length,
    summary: {
      total_orders: orders.length,
      by_status: byStatus,
      total_revenue: orders.reduce((sum, order) => sum + (order.total_amount || 0), 0),
    },
  };
}

class OrderService {
  private readonly baseUrl = 'http://localhost:8088'; // Orders service port

  // ==================== CORE ORDER ENDPOINTS ====================

  /**
   * Get today's orders for an establishment
   * GET /orders/today
   */
  async getTodayOrders(params: TodayOrdersParams): Promise<TodayOrdersResponse> {
    try {
      // Validate required parameters
      if (!params.establishment_id) {
        throw new Error('ID do estabelecimento é obrigatório');
      }

      // Mock data disabled - using real API

      let url = ROUTES.ORDERS.TODAY;
      const queryParams = new URLSearchParams();

      // Add required parameters
      queryParams.append('establishment_id', params.establishment_id);

      // Add optional parameters
      if (params.status) queryParams.append('status', params.status);
      if (params.interval_id) queryParams.append('intervalo', params.interval_id);
      // if (params.page) queryParams.append('page', params.page.toString());
      // if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.orderType && params.orderType == 'lunchbox') queryParams.append('channel', params.orderType);
      // if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      // if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const data = await apiClient.get<TodayOrdersResponse | Order[]>(url);
      return normalizeTodayOrders(data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao obter pedidos de hoje');
    }
  }

  /**
   * Get active intervals for an establishment
   * GET /orders/intervals/current
   */
  async getCurrentIntervals(establishment_id: string): Promise<IntervalResponse> {
    try {
      // Validate required parameters
      if (!establishment_id) {
        throw new Error('ID do estabelecimento é obrigatório');
      }

      // Mock data disabled - using real API

      let url = ROUTES.ORDERS.INTERVALS;
      const queryParams = new URLSearchParams();

      // Add required parameters
      queryParams.append('establishment_id', establishment_id);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      return await apiClient.get<IntervalResponse>(url);
    } catch (error) {
      throw this.handleError(error, 'Erro ao obter intervalos ativos');
    }
  }

  /**
   * Get order summary for an establishment
   * GET /orders/summary
   */
  async getOrderSummary(params: OrderSummaryParams): Promise<OrderSummaryResponse> {
    try {
      // Validate required parameters
      if (!params.establishment_id) {
        throw new Error('ID do estabelecimento é obrigatório');
      }

      let url = ROUTES.ORDERS.SUMMARY;
      const queryParams = new URLSearchParams();

      // Add required parameters
      queryParams.append('establishment_id', params.establishment_id);

      // Add optional parameters
      if (params.date) queryParams.append('date', params.date);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      return await apiClient.get<OrderSummaryResponse>(url);
    } catch (error) {
      throw this.handleError(error, 'Erro ao obter resumo de pedidos');
    }
  }

  /**
   * Get comprehensive order summary for an establishment
   * GET /orders/summary/all
   */
  async getOrderSummaryAll(params: OrderSummaryAllParams): Promise<OrderSummaryAllResponse> {
    try {
      // Validate required parameters
      if (!params.establishment_id) {
        throw new Error('ID do estabelecimento é obrigatório');
      }

      let url = ROUTES.ORDERS.SUMMARY_ALL;
      const queryParams = new URLSearchParams();

      // Add required parameters
      queryParams.append('establishment_id', params.establishment_id);

      // Add optional parameters
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      return await apiClient.get<OrderSummaryAllResponse>(url);
    } catch (error) {
      throw this.handleError(error, 'Erro ao obter resumo completo de pedidos');
    }
  }

  /**
   * Update order status
   * PATCH /orders/update-status
   */
  async updateOrderStatus(orderId: string, data: UpdateOrderStatusRequest): Promise<UpdateOrderStatusResponse> {
    try {
      // Validate parameters
      if (!orderId) {
        throw new Error('ID do pedido é obrigatório');
      }

      if (!data.status) {
        throw new Error('Status do pedido é obrigatório');
      }

      // Validate status transition
      this.validateStatusTransition(data.status);

      let url = `/orders/update-status?id=${orderId}`;

      return await apiClient.patch<UpdateOrderStatusResponse>(url, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao atualizar status do pedido');
    }
  }

  /**
   * Get detailed order information
   * GET /orders/detail
   */
  async getOrderDetail(params: OrderDetailParams): Promise<OrderDetailResponse> {
    try {
      // Validate required parameters
      if (!params.id) {
        throw new Error('ID do pedido é obrigatório');
      }

      let url = ROUTES.ORDERS.DETAIL;
      const queryParams = new URLSearchParams();

      // Add required parameters
      queryParams.append('id', params.id);

      // Add optional parameters
      if (params.include_items !== undefined) {
        queryParams.append('include_items', params.include_items.toString());
      }
      if (params.include_history !== undefined) {
        queryParams.append('include_history', params.include_history.toString());
      }

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      return await apiClient.get<OrderDetailResponse>(url);
    } catch (error) {
      throw this.handleError(error, 'Erro ao obter detalhes do pedido');
    }
  }

  // ==================== CONVENIENCE METHODS ====================

  /**
   * Get order by ID (simplified method)
   */
  async getOrderById(orderId: string): Promise<Order | null> {
    try {
      const response = await this.getOrderDetail({
        id: orderId,
        include_items: true,
        include_history: false,
      });

      return response.order || null;
    } catch (error) {
      wsLog.error('Error getting order by ID:', error);
      return null;
    }
  }

  /**
   * Check if order exists
   */
  async orderExists(orderId: string): Promise<boolean> {
    const order = await this.getOrderById(orderId);
    return order !== null;
  }

  /**
   * Get orders by status for establishment
   */
  async getOrdersByStatus(establishmentId: string, status: OrderStatus): Promise<TodayOrdersResponse> {
    return this.getTodayOrders({
      establishment_id: establishmentId,
      status,
    });
  }

  /**
   * Get pending orders for establishment
   */
  async getPendingOrders(establishmentId: string): Promise<TodayOrdersResponse> {
    return this.getOrdersByStatus(establishmentId, OrderStatus.PENDING);
  }

  /**
   * Get orders in preparation for establishment
   */
  async getOrdersInPreparation(establishmentId: string): Promise<TodayOrdersResponse> {
    return this.getOrdersByStatus(establishmentId, OrderStatus.IN_PREPARATION);
  }

  /**
   * Get ready orders for establishment
   */
  async getReadyOrders(establishmentId: string): Promise<TodayOrdersResponse> {
    return this.getOrdersByStatus(establishmentId, OrderStatus.READY);
  }

  /**
   * Get delivered orders for establishment
   */
  async getDeliveredOrders(establishmentId: string): Promise<TodayOrdersResponse> {
    return this.getOrdersByStatus(establishmentId, OrderStatus.DELIVERED);
  }

  /**
   * Get orders for a specific student
   */
  async getStudentOrders(establishmentId: string, studentId: string): Promise<Order[]> {
    try {
      const response = await this.getTodayOrders({
        establishment_id: establishmentId,
      });

      // Filter by student ID (this would ideally be done on the server)
      return response.orders.filter((order) => order.student_id === studentId);
    } catch (error) {
      wsLog.error('Error getting student orders:', error);
      return [];
    }
  }

  /**
   * Get kitchen dashboard data
   */
  async getKitchenDashboard(establishmentId: string): Promise<KitchenDashboard> {
    try {
      const [pendingResponse, inPrepResponse, readyResponse] = await Promise.all([
        this.getPendingOrders(establishmentId),
        this.getOrdersInPreparation(establishmentId),
        this.getReadyOrders(establishmentId),
      ]);

      // Calculate kitchen metrics
      const allOrders = [...pendingResponse.orders, ...inPrepResponse.orders, ...readyResponse.orders];

      const totalPreparationTime = allOrders.filter((order) => order.preparation_time).reduce((sum, order) => sum + (order.preparation_time || 0), 0);

      const ordersWithPrepTime = allOrders.filter((order) => order.preparation_time).length;
      const averagePreparationTime = ordersWithPrepTime > 0 ? totalPreparationTime / ordersWithPrepTime : 30; // Default 30 minutes

      const now = new Date();
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
      const recentOrders = allOrders.filter((order) => new Date(order.created_at) >= oneHourAgo);

      return {
        pending_orders: pendingResponse.orders.map(this.convertToKitchenOrder),
        in_preparation: inPrepResponse.orders.map(this.convertToKitchenOrder),
        ready_orders: readyResponse.orders.map(this.convertToKitchenOrder),
        average_preparation_time: averagePreparationTime,
        orders_per_hour: recentOrders.length,
        efficiency_score: this.calculateEfficiencyScore(allOrders),
      };
    } catch (error) {
      throw this.handleError(error, 'Erro ao obter dados da cozinha');
    }
  }

  // ==================== STATUS MANAGEMENT ====================

  /**
   * Mark order as in preparation
   */
  async startPreparation(orderId: string): Promise<UpdateOrderStatusResponse> {
    return this.updateOrderStatus(orderId, {
      status: OrderStatus.IN_PREPARATION,
      notes: 'Pedido iniciado na cozinha',
    });
  }

  /**
   * Mark order as ready
   */
  async markOrderReady(orderId: string): Promise<UpdateOrderStatusResponse> {
    return this.updateOrderStatus(orderId, {
      status: OrderStatus.READY,
      notes: 'Pedido pronto para entrega',
    });
  }

  /**
   * Mark order as delivered
   */
  async markOrderDelivered(orderId: string, deliveryTime?: string): Promise<UpdateOrderStatusResponse> {
    return this.updateOrderStatus(orderId, {
      status: OrderStatus.DELIVERED,
      delivery_time: deliveryTime || new Date().toISOString(),
      notes: 'Pedido entregue com sucesso',
    });
  }

  /**
   * Cancel order
   */
  async cancelOrder(orderId: string, reason?: string): Promise<UpdateOrderStatusResponse> {
    return this.updateOrderStatus(orderId, {
      status: OrderStatus.CANCELLED,
      notes: reason || 'Pedido cancelado',
    });
  }

  // ==================== BATCH OPERATIONS ====================

  /**
   * Update multiple orders status at once
   */
  async batchUpdateStatus(orderIds: string[], status: OrderStatus, notes?: string): Promise<BatchOrderResult> {
    try {
      const results: Order[] = [];
      const errors: Array<{ order_id: string; error: string }> = [];
      let successful = 0;
      let failed = 0;

      for (const orderId of orderIds) {
        try {
          const result = await this.updateOrderStatus(orderId, {
            status,
            notes,
          });
          results.push(result.order);
          successful++;
        } catch (error) {
          failed++;
          errors.push({
            order_id: orderId,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
          });
        }
      }

      return {
        successful,
        failed,
        errors,
        results,
      };
    } catch (error) {
      throw this.handleError(error, 'Erro ao atualizar múltiplos pedidos');
    }
  }

  /**
   * Cancel multiple orders
   */
  async batchCancelOrders(orderIds: string[], reason?: string): Promise<BatchOrderResult> {
    return this.batchUpdateStatus(orderIds, OrderStatus.CANCELLED, reason);
  }

  // ==================== STATISTICS AND REPORTING ====================

  /**
   * Calculate order statistics from available data
   */
  async getOrderStats(establishmentId: string, dateFrom?: string, dateTo?: string): Promise<OrderStats> {
    try {
      let summaryResponse: OrderSummaryAllResponse;

      if (dateFrom || dateTo) {
        summaryResponse = await this.getOrderSummaryAll({
          establishment_id: establishmentId,
          date_from: dateFrom,
          date_to: dateTo,
        });
      } else {
        // Get today's data
        const todayResponse = await this.getTodayOrders({
          establishment_id: establishmentId,
        });

        // Convert to summary format
        const orders = todayResponse.orders;
        const totalRevenue = orders.reduce((sum, order) => sum + order.total_amount, 0);

        return {
          total_orders: orders.length,
          total_revenue: totalRevenue,
          average_order_value: orders.length > 0 ? totalRevenue / orders.length : 0,
          by_status: todayResponse.summary.by_status,
          by_establishment: { [establishmentId]: orders.length },
          by_payment_method: this.calculatePaymentMethodStats(orders),
          by_date: { [new Date().toISOString().split('T')[0]]: orders.length },
          peak_hours: this.calculatePeakHours(orders),
          top_products: this.calculateTopProducts(orders),
          completion_times: this.calculateCompletionTimes(orders),
        };
      }

      // Process comprehensive summary data
      const totalStats = summaryResponse.total_period;

      return {
        total_orders: totalStats.total_orders,
        total_revenue: totalStats.total_revenue,
        average_order_value: totalStats.average_daily_revenue / totalStats.average_daily_orders,
        by_status: summaryResponse.trends.status_distribution,
        by_establishment: { [establishmentId]: totalStats.total_orders },
        by_payment_method: {},
        by_date: this.convertTrendsToDateStats(summaryResponse.trends.daily_orders),
        peak_hours: [],
        top_products: [],
        completion_times: {
          average_preparation_time: 30,
          average_delivery_time: 45,
          by_status: summaryResponse.trends.status_distribution,
        },
      };
    } catch (error) {
      throw this.handleError(error, 'Erro ao obter estatísticas de pedidos');
    }
  }

  // ==================== VALIDATION METHODS ====================

  /**
   * Validate order status transition
   */
  private validateStatusTransition(newStatus: OrderStatus, currentStatus?: OrderStatus): void {
    const rules = ORDER_VALIDATION_RULES;

    if (!Object.values(OrderStatus).includes(newStatus)) {
      throw new Error(`Status inválido: ${newStatus}`);
    }

    if (currentStatus) {
      const allowedTransitions = rules.status.allowed_transitions[currentStatus] || [];
      if (!allowedTransitions.includes(newStatus)) {
        throw new Error(`Transição de status inválida: de ${currentStatus} para ${newStatus}`);
      }
    }
  }

  /**
   * Validate UUID format
   */
  private isValidUUID(uuid: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Calculate total amount for order items
   */
  static calculateOrderTotal(items: OrderItem[]): number {
    return items.reduce((total, item) => total + item.total_price, 0);
  }

  /**
   * Get status options for forms/dropdowns
   */
  static getStatusOptions(): Array<{ value: OrderStatus; label: string }> {
    return [
      { value: OrderStatus.PENDING, label: 'Pendente' },
      { value: OrderStatus.IN_PREPARATION, label: 'Em Preparo' },
      { value: OrderStatus.READY, label: 'Pronto' },
      { value: OrderStatus.DELIVERED, label: 'Entregue' },
      { value: OrderStatus.CANCELLED, label: 'Cancelado' },
    ];
  }

  /**
   * Format order number for display
   */
  static formatOrderNumber(orderNumber: string): string {
    return orderNumber.padStart(6, '0');
  }

  /**
   * Get order priority based on time and status
   */
  static getOrderPriority(order: Order): 'low' | 'normal' | 'high' | 'urgent' {
    const now = new Date();
    const orderTime = new Date(order.created_at);
    const minutesElapsed = Math.floor((now.getTime() - orderTime.getTime()) / (1000 * 60));

    if (order.status === OrderStatus.PENDING && minutesElapsed > 30) {
      return 'urgent';
    }
    if (order.status === OrderStatus.IN_PREPARATION && minutesElapsed > 45) {
      return 'high';
    }
    if (minutesElapsed > 15) {
      return 'normal';
    }
    return 'low';
  }

  /**
   * Convert Order to KitchenOrder format
   */
  private convertToKitchenOrder(order: Order) {
    return {
      id: order.id,
      order_number: order.order_number,
      items: order.items,
      status: order.status,
      priority: OrderService.getOrderPriority(order),
      estimated_time: order.preparation_time || 30,
      notes: order.notes || '',
      student_name: order.student_name || 'Cliente',
      created_at: order.created_at,
      started_at: order.status === OrderStatus.IN_PREPARATION ? order.updated_at : undefined,
      special_requirements: this.extractSpecialRequirements(order),
    };
  }

  /**
   * Extract special requirements from order
   */
  private extractSpecialRequirements(order: Order): string[] {
    const requirements: string[] = [];

    if (order.notes?.toLowerCase().includes('sem cebola')) {
      requirements.push('Sem cebola');
    }
    if (order.notes?.toLowerCase().includes('sem tomate')) {
      requirements.push('Sem tomate');
    }
    if (order.special_instructions) {
      requirements.push(order.special_instructions);
    }

    return requirements;
  }

  /**
   * Calculate efficiency score based on orders
   */
  private calculateEfficiencyScore(orders: Order[]): number {
    if (orders.length === 0) return 100;

    const now = new Date();
    let totalScore = 0;

    orders.forEach((order) => {
      const orderTime = new Date(order.created_at);
      const minutesElapsed = Math.floor((now.getTime() - orderTime.getTime()) / (1000 * 60));

      let score = 100;
      if (order.status === OrderStatus.PENDING && minutesElapsed > 15) {
        score -= Math.min(50, minutesElapsed - 15);
      }
      if (order.status === OrderStatus.IN_PREPARATION && minutesElapsed > 30) {
        score -= Math.min(30, minutesElapsed - 30);
      }

      totalScore += Math.max(0, score);
    });

    return Math.round(totalScore / orders.length);
  }

  /**
   * Calculate payment method statistics
   */
  private calculatePaymentMethodStats(orders: Order[]): Record<string, number> {
    const stats: Record<string, number> = {};

    orders.forEach((order) => {
      const method = order.payment_method || 'Não informado';
      stats[method] = (stats[method] || 0) + 1;
    });

    return stats;
  }

  /**
   * Calculate peak hours from orders
   */
  private calculatePeakHours(orders: Order[]): Array<{ hour: number; order_count: number; percentage: number }> {
    const hourStats: Record<number, number> = {};

    orders.forEach((order) => {
      const hour = new Date(order.created_at).getHours();
      hourStats[hour] = (hourStats[hour] || 0) + 1;
    });

    const total = orders.length;
    return Object.entries(hourStats)
      .map(([hour, count]) => ({
        hour: parseInt(hour),
        order_count: count,
        percentage: Math.round((count / total) * 100),
      }))
      .sort((a, b) => b.order_count - a.order_count)
      .slice(0, 5);
  }

  /**
   * Calculate top products from orders
   */
  private calculateTopProducts(orders: Order[]): Array<{
    product_id: string;
    product_name: string;
    quantity_sold: number;
    revenue: number;
    order_count: number;
  }> {
    const productStats: Record<
      string,
      {
        name: string;
        quantity: number;
        revenue: number;
        orderCount: number;
      }
    > = {};

    orders.forEach((order) => {
      order.items.forEach((item) => {
        if (!productStats[item.product_id]) {
          productStats[item.product_id] = {
            name: item.product_name,
            quantity: 0,
            revenue: 0,
            orderCount: 0,
          };
        }

        productStats[item.product_id].quantity += item.quantity;
        productStats[item.product_id].revenue += item.total_price;
        productStats[item.product_id].orderCount += 1;
      });
    });

    return Object.entries(productStats)
      .map(([productId, data]) => ({
        product_id: productId,
        product_name: data.name,
        quantity_sold: data.quantity,
        revenue: data.revenue,
        order_count: data.orderCount,
      }))
      .sort((a, b) => b.quantity_sold - a.quantity_sold)
      .slice(0, 10);
  }

  /**
   * Calculate completion times
   */
  private calculateCompletionTimes(orders: Order[]): {
    average_preparation_time: number;
    average_delivery_time: number;
    by_status: Record<OrderStatus, number>;
  } {
    const completedOrders = orders.filter((order) => order.status === OrderStatus.DELIVERED && order.delivered_at);

    let totalPrepTime = 0;
    let totalDeliveryTime = 0;

    completedOrders.forEach((order) => {
      const createdTime = new Date(order.created_at).getTime();
      const deliveredTime = new Date(order.delivered_at!).getTime();

      const totalMinutes = Math.floor((deliveredTime - createdTime) / (1000 * 60));
      totalPrepTime += totalMinutes;
      totalDeliveryTime += totalMinutes;
    });

    const avgPrepTime = completedOrders.length > 0 ? totalPrepTime / completedOrders.length : 30;
    const avgDeliveryTime = completedOrders.length > 0 ? totalDeliveryTime / completedOrders.length : 45;

    const statusCounts: Record<OrderStatus, number> = {
      [OrderStatus.PENDING]: 0,
      [OrderStatus.IN_PREPARATION]: 0,
      [OrderStatus.READY]: 0,
      [OrderStatus.DELIVERED]: 0,
      [OrderStatus.CANCELLED]: 0,
    };

    orders.forEach((order) => {
      if (order.status) {
        statusCounts[order.status]++;
      }
    });

    return {
      average_preparation_time: Math.round(avgPrepTime),
      average_delivery_time: Math.round(avgDeliveryTime),
      by_status: statusCounts,
    };
  }

  /**
   * Convert trends data to date statistics
   */
  private convertTrendsToDateStats(trends: Array<{ date: string; count: number }>): Record<string, number> {
    const stats: Record<string, number> = {};
    trends.forEach((trend) => {
      stats[trend.date] = trend.count;
    });
    return stats;
  }

  // ==================== ERROR HANDLING ====================

  /**
   * Handle order-specific errors
   */
  private handleError(error: unknown, defaultMessage: string): ApiError {
    if (error && typeof error === 'object' && 'message' in error) {
      const apiError = error as ApiError;

      // Handle specific HTTP status codes
      switch (apiError.status) {
        case 400:
          return {
            ...apiError,
            message: 'Dados do pedido inválidos',
          };
        case 401:
          return {
            ...apiError,
            message: 'Não autorizado para gerenciar pedidos',
          };
        case 403:
          return {
            ...apiError,
            message: 'Acesso negado ao sistema de pedidos',
          };
        case 404:
          return {
            ...apiError,
            message: 'Pedido não encontrado',
          };
        case 409:
          return {
            ...apiError,
            message: 'Conflito: não é possível alterar o status do pedido',
          };
        case 422:
          return {
            ...apiError,
            message: 'Dados de entrada inválidos para pedido',
          };
        case 500:
          return {
            ...apiError,
            message: 'Erro interno do servidor de pedidos',
          };
        default:
          return {
            ...apiError,
            message: apiError.message || defaultMessage,
          };
      }
    }

    // Handle validation errors
    if (error instanceof Error) {
      return {
        message: error.message,
        status: 400,
      } as ApiError;
    }

    return {
      message: defaultMessage,
      status: 0,
    } as ApiError;
  }
}

// Export singleton instance
export const orderService = new OrderService();

// Export class for testing
export { OrderService };
export default orderService;
